import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

const SUB_CAPTIONS = [
    "Analyzing niche signals…",
    "Mapping audience hooks…",
    "Routing posts across platforms…",
    "Drafting daily content narrative…",
    "Compounding week-over-week milestones…",
    "Finalizing roadmap…",
];

export default function StrategyLoading() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => {
            setIdx((i) => (i + 1) % SUB_CAPTIONS.length);
        }, 2500);
        return () => clearInterval(t);
    }, []);

    return (
        <div
            className="relative mx-auto flex min-h-[420px] max-w-[640px] flex-col items-center justify-center gap-7 overflow-hidden rounded-3xl border border-[#1F2A40] bg-[#111726] px-10 py-16 text-center"
            data-testid="strategy-loading"
        >
            {/* Background orb glow */}
            <div
                className="pointer-events-none absolute inset-0"
                aria-hidden
                style={{
                    background:
                        "radial-gradient(circle at 50% 55%, rgba(47,88,205,0.18) 0%, transparent 55%), radial-gradient(circle at 60% 30%, rgba(0,245,212,0.09) 0%, transparent 60%)",
                }}
            />

            {/* Pulsing orb — electric blue → mint wash */}
            <div className="relative flex h-24 w-24 items-center justify-center">
                <motion.span
                    className="absolute inset-0 rounded-full"
                    animate={{
                        scale: [1, 1.35, 1],
                        opacity: [0.3, 0.75, 0.3],
                    }}
                    transition={{
                        duration: 2.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        background:
                            "radial-gradient(circle, rgba(0,245,212,0.6) 0%, rgba(47,88,205,0.3) 55%, transparent 75%)",
                        filter: "blur(12px)",
                    }}
                />
                <motion.div
                    className="relative h-14 w-14 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "linear",
                    }}
                    style={{
                        background:
                            "conic-gradient(from 0deg, #2F58CD, #00F5D4, #2F58CD)",
                        maskImage:
                            "radial-gradient(circle, transparent 50%, black 54%)",
                        WebkitMaskImage:
                            "radial-gradient(circle, transparent 50%, black 54%)",
                    }}
                />
            </div>

            <div className="relative flex flex-col items-center gap-2">
                <h3
                    className="text-xl font-medium tracking-wide text-[#E8ECF5]"
                    data-testid="strategy-loading-title"
                >
                    AI Agent orchestrating your personalized strategy…
                </h3>
                <AnimatePresence mode="wait">
                    <motion.p
                        key={idx}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.4 }}
                        className="min-h-[18px] font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F5D4]"
                        data-testid="strategy-loading-sub"
                    >
                        {SUB_CAPTIONS[idx]}
                    </motion.p>
                </AnimatePresence>
            </div>

            {/* Progress worm */}
            <div className="relative h-[2px] w-full max-w-sm overflow-hidden rounded-full bg-[#1F2A40]">
                <motion.span
                    className="absolute left-0 top-0 h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-[#00F5D4] to-transparent"
                    animate={{ x: ["-100%", "400%"] }}
                    transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
            </div>

            <p
                className="relative max-w-md font-mono text-[11px] italic text-[#8A93A6]"
                data-testid="strategy-loading-footer"
            >
                Typical generation time is 30–60 seconds. Hang tight — the
                roadmap is only drafted once, then yours to copy and tweak.
            </p>
        </div>
    );
}
